/**
 * GUMSHOE — fixed-timestep game loop with interpolated render callbacks.
 * Frozen core.
 */
import { FIXED_TIMESTEP, MAX_FRAME_DELTA } from './constants'
import type { GameLoop } from './types'

type UpdateFn = (dt: number) => void
type RenderFn = (alpha: number) => void

export const createGameLoop = (fixedDt: number = FIXED_TIMESTEP): GameLoop => {
  const updateFns = new Set<UpdateFn>()
  const renderFns = new Set<RenderFn>()

  let running = false
  let rafId = 0
  let lastTime = 0
  let accumulator = 0

  const frame = (now: number): void => {
    if (!running) return
    const delta = Math.min((now - lastTime) / 1000, MAX_FRAME_DELTA)
    lastTime = now
    accumulator += delta

    while (accumulator >= fixedDt) {
      for (const fn of updateFns) fn(fixedDt)
      accumulator -= fixedDt
    }

    const alpha = accumulator / fixedDt
    for (const fn of renderFns) fn(alpha)

    rafId = requestAnimationFrame(frame)
  }

  return {
    onUpdate(fn: UpdateFn): () => void {
      updateFns.add(fn)
      return () => updateFns.delete(fn)
    },
    onRender(fn: RenderFn): () => void {
      renderFns.add(fn)
      return () => renderFns.delete(fn)
    },
    start(): void {
      if (running) return
      running = true
      lastTime = performance.now()
      accumulator = 0
      rafId = requestAnimationFrame(frame)
    },
    stop(): void {
      running = false
      cancelAnimationFrame(rafId)
    },
    get running(): boolean {
      return running
    }
  }
}
